import { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { 
  Zap, 
  Plus, 
  Edit, 
  Trash2, 
  Search,
  Save,
  X,
  Loader2,
  Filter,
  ArrowUpDown,
  ArrowUp,
  ArrowDown
} from "lucide-react";
import { usePermissions } from "@/hooks/usePermissions";
import { useIsMobile } from "@/hooks/use-mobile";
import { Inversor, CreateInversor, InversorArquiteturaRede } from "@/types/inversores";
import { inversoresService } from "@/services/inversoresService";
import { toast } from "sonner";
import { resolveAllowedImageMimeType } from "@/utils/imageMime";

const LABEL_ARQUITETURA_REDE: Record<InversorArquiteturaRede, string> = {
  ongrid: "On-grid",
  hibrido: "Híbrido",
};

export default function Inversores() {
  const permissions = usePermissions();
  // Nesta página, consideramos "mobile" para larguras abaixo de 990px
  const isMobile = useIsMobile(990);
  const [inversores, setInversores] = useState<Inversor[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingInversor, setEditingInversor] = useState<Inversor | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [sortColumn, setSortColumn] = useState<string>('marca');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [filterOpen, setFilterOpen] = useState(false);
  const [appliedFilter, setAppliedFilter] = useState({ marca: '', tipoConexao: '', potencia: '', microinversor: '' });
  const [formData, setFormData] = useState<CreateInversor>({
    marca: "",
    modelo: "",
    potenciaNominal: 0,
    potenciaMaxima: 0,
    tensaoMaxima: 0,
    tensaoPartida: 0,
    faixaTensaoMPPT: { minima: 0, maxima: 0 },
    tensaoNominal: 0,
    correnteMaximaMPPT: 0,
    mpptIndependentes: 0,
    stringsPorMPPT: 0,
    potenciaAparenteMaxima: 0,
    correnteMaximaSaida: 0,
    tensaoOperacao: 0,
    frequencia: 0,
    fatorPotencia: 0,
    tipoConexao: "",
    garantia: 0,
    microinversor: false,
    arquiteturaRede: "ongrid",
    imagemUrl: null
  });
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const imagemInversorInputRef = useRef<HTMLInputElement>(null);
  const [potenciaNominalKwStr, setPotenciaNominalKwStr] = useState("");

  // Carregar inversores do Supabase
  useEffect(() => {
    loadInversores();
  }, []);

  const loadInversores = async () => {
    try {
      setIsLoading(true);
      const data = await inversoresService.getAll();
      setInversores(data);
    } catch (error) {
      console.error('Erro ao carregar inversores:', error);
      toast.error('Erro ao carregar inversores');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string | number) => {
    if (field.startsWith('faixaTensaoMPPT.')) {
      const mpptField = field.split('.')[1];
      const numValue = Number(value);
      if (!isNaN(numValue)) {
        setFormData(prev => ({
          ...prev,
          faixaTensaoMPPT: {
            ...prev.faixaTensaoMPPT,
            [mpptField]: numValue
          }
        }));
      }
    } else {
      // Para campos numéricos, garantir que o valor seja um número válido
      if (typeof value === 'string' && value.trim() === '') {
        // Se o campo estiver vazio, usar 0 como padrão
        setFormData(prev => ({
          ...prev,
          [field]: 0
        }));
      } else {
        const numValue = Number(value);
        if (!isNaN(numValue)) {
          setFormData(prev => ({
            ...prev,
            [field]: numValue
          }));
        } else {
          // Se não for um número válido, manter o valor como string (para campos de texto)
          setFormData(prev => ({
            ...prev,
            [field]: value
          }));
        }
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Verificar se o usuário tem permissão
    if (permissions.isPartner()) {
      toast.error('Acesso negado. Parceiros não podem adicionar ou editar inversores.');
      return;
    }
    
    try {
      setIsSubmitting(true);
      
      // Validações adicionais
      if (!formData.marca.trim()) {
        toast.error('Marca é obrigatória');
        return;
      }
      
      if (!formData.modelo.trim()) {
        toast.error('Modelo é obrigatório');
        return;
      }
      
      if (formData.potenciaNominal <= 0) {
        toast.error('Potência nominal deve ser maior que zero');
        return;
      }
      
      if (formData.faixaTensaoMPPT.minima >= formData.faixaTensaoMPPT.maxima) {
        toast.error('Tensão MPPT mínima deve ser menor que a máxima');
        return;
      }
      
      console.log('Enviando dados do formulário:', formData);
      
      if (editingInversor) {
        // Atualizar inversor existente
        let imagemUrl = formData.imagemUrl;
        
        // Fazer upload da imagem se houver uma nova imagem selecionada
        if (selectedImage) {
          try {
            imagemUrl = await inversoresService.uploadImagem(editingInversor.id, selectedImage);
          } catch (error) {
            console.error('Erro ao fazer upload da imagem:', error);
            toast.error('Erro ao fazer upload da imagem');
            return;
          }
        }
        
        const updatedInversor = await inversoresService.update(editingInversor.id, { ...formData, imagemUrl });
        setInversores(prev => prev.map(i => 
          i.id === editingInversor.id ? updatedInversor : i
        ));
        toast.success('Inversor atualizado com sucesso!');
      } else {
        // Adicionar novo inversor
        // Primeiro criar o inversor sem imagem
        const newInversor = await inversoresService.create({ ...formData, imagemUrl: null });
        
        // Depois fazer upload da imagem se houver
        if (selectedImage) {
          try {
            const imagemUrl = await inversoresService.uploadImagem(newInversor.id, selectedImage);
            // Atualizar com a URL da imagem
            const updatedInversor = await inversoresService.update(newInversor.id, { imagemUrl });
            setInversores(prev => prev.map(i => i.id === newInversor.id ? updatedInversor : i));
          } catch (error) {
            console.error('Erro ao fazer upload da imagem:', error);
            toast.error('Inversor criado, mas houve erro ao fazer upload da imagem');
            // Mesmo com erro no upload, adicionar o inversor à lista
            setInversores(prev => [...prev, newInversor]);
            resetForm();
            setIsDialogOpen(false);
            return;
          }
        } else {
          setInversores(prev => [...prev, newInversor]);
        }
        
        toast.success('Inversor criado com sucesso!');
      }
      
      resetForm();
      setIsDialogOpen(false);
    } catch (error: unknown) {
      console.error('Erro detalhado ao salvar inversor:', error);
      
      // Tratamento de erro mais específico
      let errorMessage = 'Erro ao salvar inversor';
      if (error && typeof error === 'object' && 'message' in error && typeof error.message === 'string') {
        errorMessage = error.message;
      } else if (error && typeof error === 'object' && 'details' in error && typeof error.details === 'string') {
        errorMessage = `Erro de validação: ${error.details}`;
      } else if (error && typeof error === 'object' && 'hint' in error && typeof error.hint === 'string') {
        errorMessage = `Erro: ${error.hint}`;
      }
      
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (inversor: Inversor) => {
    // Verificar se o usuário tem permissão
    if (permissions.isPartner()) {
      toast.error('Acesso negado. Parceiros não podem editar inversores.');
      return;
    }

    setEditingInversor(inversor);
    setFormData({
      marca: inversor.marca,
      modelo: inversor.modelo,
      potenciaNominal: inversor.potenciaNominal,
      potenciaMaxima: inversor.potenciaMaxima,
      tensaoMaxima: inversor.tensaoMaxima,
      tensaoPartida: inversor.tensaoPartida,
      faixaTensaoMPPT: inversor.faixaTensaoMPPT,
      tensaoNominal: inversor.tensaoNominal,
      correnteMaximaMPPT: inversor.correnteMaximaMPPT,
      mpptIndependentes: inversor.mpptIndependentes,
      stringsPorMPPT: inversor.stringsPorMPPT,
      potenciaAparenteMaxima: inversor.potenciaAparenteMaxima,
      correnteMaximaSaida: inversor.correnteMaximaSaida,
      tensaoOperacao: inversor.tensaoOperacao,
      frequencia: inversor.frequencia,
      fatorPotencia: inversor.fatorPotencia,
      tipoConexao: inversor.tipoConexao,
      garantia: inversor.garantia,
      microinversor: inversor.microinversor ?? false,
      arquiteturaRede: inversor.arquiteturaRede ?? "ongrid",
      imagemUrl: inversor.imagemUrl || null
    });
    setSelectedImage(null);
    setImagePreview(inversor.imagemUrl || null);
    setPotenciaNominalKwStr(inversor.potenciaNominal > 0 ? String(inversor.potenciaNominal / 1000) : "");
    setIsDialogOpen(true);
  };

  const handleDelete = async (id: string) => {
    // Verificar se o usuário tem permissão
    if (permissions.isPartner()) {
      toast.error('Acesso negado. Parceiros não podem excluir inversores.');
      return;
    }

    if (confirm("Tem certeza que deseja excluir este inversor?")) {
      try {
        await inversoresService.delete(id);
        setInversores(prev => prev.filter(i => i.id !== id));
        toast.success('Inversor excluído com sucesso!');
      } catch (error) {
        console.error('Erro ao excluir inversor:', error);
        toast.error('Erro ao excluir inversor');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      marca: "",
      modelo: "",
      potenciaNominal: 0,
      potenciaMaxima: 0,
      tensaoMaxima: 0,
      tensaoPartida: 0,
      faixaTensaoMPPT: { minima: 0, maxima: 0 },
      tensaoNominal: 0,
      correnteMaximaMPPT: 0,
      mpptIndependentes: 0,
      stringsPorMPPT: 0,
      potenciaAparenteMaxima: 0,
      correnteMaximaSaida: 0,
      tensaoOperacao: 0,
      frequencia: 0,
      fatorPotencia: 0,
      tipoConexao: "",
      garantia: 0,
      microinversor: false,
      arquiteturaRede: "ongrid",
      imagemUrl: null
    });
    setSelectedImage(null);
    setImagePreview(null);
    if (imagemInversorInputRef.current) {
      imagemInversorInputRef.current.value = "";
    }
    setPotenciaNominalKwStr("");
    setEditingInversor(null);
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!resolveAllowedImageMimeType(file)) {
        toast.error('Tipo de arquivo não permitido. Use JPEG, PNG, GIF ou WebP.');
        return;
      }

      // Validar tamanho (máximo 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Arquivo muito grande. Tamanho máximo: 5MB.');
        return;
      }

      setSelectedImage(file);
      
      // Criar preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
    setFormData((prev) => ({ ...prev, imagemUrl: null }));
    if (imagemInversorInputRef.current) {
      imagemInversorInputRef.current.value = "";
    }
  };

  const applyFilters = () => {
    setFilterOpen(false);
  };

  const clearFilters = () => {
    setAppliedFilter({ marca: '', tipoConexao: '', potencia: '', microinversor: '' });
    setFilterOpen(false);
  };

  const filtersApplied = Object.values(appliedFilter).some(val => val !== '' && val !== 'todos');

  const uniqueMarcas = [...new Set(inversores.map(i => i.marca || '').filter(marca => marca !== ''))];
  const uniqueTipoConexao = [...new Set(inversores.map(i => i.tipoConexao || '').filter(tipo => tipo !== ''))];
  const uniquePotencias = [...new Set(inversores.map(i => i.potenciaNominal || 0).filter(potencia => potencia !== 0))];

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const sortedInversores = [...inversores].sort((a, b) => {
    if (!sortColumn) return 0;

    let aVal: string | number;
    let bVal: string | number;

    switch (sortColumn) {
      case 'marca':
        aVal = a.marca || '';
        bVal = b.marca || '';
        return sortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      case 'modelo':
        aVal = a.modelo || '';
        bVal = b.modelo || '';
        return sortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      case 'potenciaNominal':
        aVal = a.potenciaNominal || 0;
        bVal = b.potenciaNominal || 0;
        return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
      case 'tipoConexao':
        aVal = a.tipoConexao || '';
        bVal = b.tipoConexao || '';
        return sortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      case 'mpptIndependentes':
        aVal = a.mpptIndependentes || 0;
        bVal = b.mpptIndependentes || 0;
        return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
      case 'tensaoMaxima':
        aVal = a.tensaoMaxima || 0;
        bVal = b.tensaoMaxima || 0;
        return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
      case 'arquiteturaRede':
        aVal = a.arquiteturaRede || 'ongrid';
        bVal = b.arquiteturaRede || 'ongrid';
        return sortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      default:
        return 0;
    }
  });

  const filteredInversores = sortedInversores.filter(inversor => {
    const marca = (inversor.marca || '').toLowerCase();
    const modelo = (inversor.modelo || '').toLowerCase();
    const tipoConexao = (inversor.tipoConexao || '').toLowerCase();
    const potencia = inversor.potenciaNominal || 0;

    const matchesSearch = appliedSearch === '' || 
                          marca.includes(appliedSearch.toLowerCase()) ||
                          modelo.includes(appliedSearch.toLowerCase()) ||
                          tipoConexao.includes(appliedSearch.toLowerCase());

    const matchesMicroFilter = appliedFilter.microinversor === '' || appliedFilter.microinversor === 'todos' ||
      (appliedFilter.microinversor === 'micro' && !!inversor.microinversor) ||
      (appliedFilter.microinversor === 'inversor' && !inversor.microinversor);

    const matchesFilter = (appliedFilter.marca === '' || appliedFilter.marca === 'todos' || marca === appliedFilter.marca.toLowerCase()) &&
                          (appliedFilter.tipoConexao === '' || appliedFilter.tipoConexao === 'todos' || tipoConexao === appliedFilter.tipoConexao.toLowerCase()) &&
                          (appliedFilter.potencia === '' || appliedFilter.potencia === 'todos' || potencia === Number(appliedFilter.potencia)) &&
                          matchesMicroFilter;

    return matchesSearch && matchesFilter;
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin" />
        <span className="ml-2">Carregando inversores...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 md:p-6">
      {/* Header */}
      <div className={`flex ${isMobile ? 'flex-col' : 'items-center justify-between'} gap-4`}>
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-foreground">Inversores e Micro</h1>
          <p className="text-sm md:text-base text-muted-foreground">
            Gerencie o catálogo de inversores e microinversores disponíveis
          </p>
        </div>
        <div className={`flex ${isMobile ? 'flex-wrap' : 'items-center'} gap-2`}>
          <Badge variant="outline">
            {inversores.length} inversores cadastrados
          </Badge>
        </div>
      </div>

      {/* Search and Actions */}
      <div className={`flex ${isMobile ? 'flex-col' : 'items-center justify-between'} gap-3`}>
        <form
          className={`flex ${isMobile ? 'flex-col' : 'flex-row'} gap-2 flex-1`}
          onSubmit={(e) => {
            e.preventDefault();
            setAppliedSearch(searchTerm);
          }}
        >
          <div className={`relative ${isMobile ? 'w-full' : 'w-96'}`}>
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
            <Input
              placeholder="Buscar por marca, modelo ou tipo de conexão..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <div className={`flex ${isMobile ? 'flex-row' : ''} gap-2`}>
            <Button type="submit" variant="outline" className={isMobile ? "flex-1" : ""}>
              <Search className="h-4 w-4 mr-2" /> Buscar
            </Button>
            <Button
              type="button"
              variant={filtersApplied ? "default" : "outline"}
              onClick={() => setFilterOpen(true)}
              className={isMobile ? "flex-1" : ""}
            >
              <Filter className="h-4 w-4 mr-2" /> Filtros
            </Button>
          </div>
        </form>
        
        {!permissions.isPartner() && (
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={() => resetForm()} className={isMobile ? "w-full" : ""}>
                <Plus className="h-4 w-4 mr-2" />
                Adicionar Inversor
              </Button>
            </DialogTrigger>
          <DialogContent className={`${isMobile ? 'max-w-[95vw]' : 'max-w-4xl'} max-h-[90vh] overflow-y-auto`}>
            <DialogHeader>
              <DialogTitle>
                {editingInversor ? "Editar Inversor" : "Adicionar Novo Inversor"}
              </DialogTitle>
              <DialogDescription>
                Preencha as informações técnicas do inversor
              </DialogDescription>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Informações Básicas */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Informações Básicas</CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="marca">Marca *</Label>
                    <Input
                      id="marca"
                      value={formData.marca}
                      onChange={(e) => handleInputChange("marca", e.target.value)}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="modelo">Modelo *</Label>
                    <Input
                      id="modelo"
                      value={formData.modelo}
                      onChange={(e) => handleInputChange("modelo", e.target.value)}
                      required
                    />
                  </div>
                  <div className="space-y-2 col-span-2">
                    <Label htmlFor="arquiteturaRede">Tipo de inversor (rede) *</Label>
                    <Select
                      value={formData.arquiteturaRede ?? "ongrid"}
                      onValueChange={(v) =>
                        setFormData((prev) => ({
                          ...prev,
                          arquiteturaRede: v as InversorArquiteturaRede,
                        }))
                      }
                    >
                      <SelectTrigger id="arquiteturaRede">
                        <SelectValue placeholder="Selecione" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ongrid">{LABEL_ARQUITETURA_REDE.ongrid}</SelectItem>
                        <SelectItem value="hibrido">{LABEL_ARQUITETURA_REDE.hibrido}</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-sm text-muted-foreground">
                      On-grid: conexão apenas à rede. Híbrido: equipamento híbrido (ex.: com bateria).
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="potenciaNominal">Potência Nominal (kW) *</Label>
                    <Input
                      id="potenciaNominal"
                      type="text"
                      inputMode="decimal"
                      value={potenciaNominalKwStr}
                      onChange={(e) => {
                        const raw = e.target.value.replace(',', '.');
                        setPotenciaNominalKwStr(e.target.value);
                        const num = parseFloat(raw);
                        if (raw === "" || raw === "." || raw === ",") {
                          handleInputChange("potenciaNominal", 0);
                        } else if (!isNaN(num) && num >= 0) {
                          handleInputChange("potenciaNominal", Math.round(num * 1000));
                        }
                      }}
                      onBlur={() => {
                        if (potenciaNominalKwStr.trim() !== "") {
                          const num = parseFloat(potenciaNominalKwStr.replace(',', '.'));
                          if (!isNaN(num) && num > 0) {
                            setPotenciaNominalKwStr(String(num));
                          }
                        }
                      }}
                      placeholder="Ex: 3 ou 3.5"
                      required
                    />
                  </div>
                  <div className="space-y-2 col-span-2 flex items-center justify-between rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <Label htmlFor="microinversor">O equipamento é um microinversor?</Label>
                      <p className="text-sm text-muted-foreground">
                        Marque se este inversor for do tipo microinversor
                      </p>
                    </div>
                    <Switch
                      id="microinversor"
                      checked={formData.microinversor ?? false}
                      onCheckedChange={(checked) => setFormData(prev => ({ ...prev, microinversor: checked }))}
                    />
                  </div>
                  <div className="space-y-2 col-span-2">
                    <Label htmlFor="imagem">Imagem do Inversor</Label>
                    <div className="flex flex-wrap items-center gap-2">
                      <Input
                        ref={imagemInversorInputRef}
                        id="imagem"
                        type="file"
                        accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                        onChange={handleImageChange}
                        className="cursor-pointer max-w-md flex-1 min-w-[200px]"
                      />
                      {(imagePreview || selectedImage) && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={handleRemoveImage}
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Remover imagem
                        </Button>
                      )}
                    </div>
                    {imagePreview && (
                      <div className="mt-2">
                        <img
                          src={imagePreview}
                          alt="Pré-visualização"
                          className="w-32 h-32 object-cover rounded border"
                        />
                      </div>
                    )}
                    <p className="text-sm text-muted-foreground">
                      Formatos aceitos: JPEG, PNG, GIF, WebP. Tamanho máximo: 5MB
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Dados de Entrada (CC) */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Dados de Entrada (CC)</CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="potenciaMaxima">Potência Máxima de entrada (Wp) *</Label>
                    <Input
                      id="potenciaMaxima"
                      type="number"
                      value={formData.potenciaMaxima || ""}
                      onChange={(e) => handleInputChange("potenciaMaxima", Number(e.target.value))}
                      placeholder="Ex: 3300"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="tensaoMaxima">Tensão Máxima (Vcc) *</Label>
                    <Input
                      id="tensaoMaxima"
                      type="number"
                      value={formData.tensaoMaxima || ""}
                      onChange={(e) => handleInputChange("tensaoMaxima", Number(e.target.value))}
                      placeholder="Ex: 600"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="tensaoPartida">Tensão de partida (Vcc) *</Label>
                    <Input
                      id="tensaoPartida"
                      type="number"
                      value={formData.tensaoPartida || ""}
                      onChange={(e) => handleInputChange("tensaoPartida", Number(e.target.value))}
                      placeholder="Ex: 200"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="faixaTensaoMPPTMin">Faixa de Tensão MPPT Mínima (Vcc) *</Label>
                    <Input
                      id="faixaTensaoMPPTMin"
                      type="number"
                      value={formData.faixaTensaoMPPT.minima || ""}
                      onChange={(e) => handleInputChange("faixaTensaoMPPT.minima", Number(e.target.value))}
                      placeholder="Ex: 250"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="faixaTensaoMPPTMax">Faixa de Tensão MPPT Máxima (Vcc) *</Label>
                    <Input
                      id="faixaTensaoMPPTMax"
                      type="number"
                      value={formData.faixaTensaoMPPT.maxima || ""}
                      onChange={(e) => handleInputChange("faixaTensaoMPPT.maxima", Number(e.target.value))}
                      placeholder="Ex: 500"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="tensaoNominal">Tensão Nominal (Vcc) *</Label>
                    <Input
                      id="tensaoNominal"
                      type="number"
                      value={formData.tensaoNominal || ""}
                      onChange={(e) => handleInputChange("tensaoNominal", Number(e.target.value))}
                      placeholder="Ex: 480"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="correnteMaximaMPPT">Corrente Máxima por MPPT (A) *</Label>
                    <Input
                      id="correnteMaximaMPPT"
                      type="number"
                      step="0.1"
                      value={formData.correnteMaximaMPPT || ""}
                      onChange={(e) => handleInputChange("correnteMaximaMPPT", Number(e.target.value))}
                      placeholder="Ex: 15"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="mpptIndependentes">MPPTs Independentes *</Label>
                    <Input
                      id="mpptIndependentes"
                      type="number"
                      value={formData.mpptIndependentes || ""}
                      onChange={(e) => handleInputChange("mpptIndependentes", Number(e.target.value))}
                      placeholder="Ex: 1"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="stringsPorMPPT">Strings por MPPT *</Label>
                    <Input
                      id="stringsPorMPPT"
                      type="number"
                      value={formData.stringsPorMPPT || ""}
                      onChange={(e) => handleInputChange("stringsPorMPPT", Number(e.target.value))}
                      placeholder="Ex: 1"
                      required
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Dados de Saída (CA) */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Dados de Saída (CA)</CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="potenciaNominalW">Potência Nominal (W) *</Label>
                    <Input
                      id="potenciaNominalW"
                      type="number"
                      value={formData.potenciaNominal || ""}
                      onChange={(e) => {
                        const val = Number(e.target.value);
                        handleInputChange("potenciaNominal", val);
                        setPotenciaNominalKwStr(val > 0 ? String(val / 1000) : "");
                      }}
                      placeholder="Ex: 3000"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="potenciaAparenteMaxima">Potência Aparente Máxima (VA) *</Label>
                    <Input
                      id="potenciaAparenteMaxima"
                      type="number"
                      value={formData.potenciaAparenteMaxima || ""}
                      onChange={(e) => handleInputChange("potenciaAparenteMaxima", Number(e.target.value))}
                      placeholder="Ex: 3500"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="correnteMaximaSaida">Corrente Máxima de saída (A) *</Label>
                    <Input
                      id="correnteMaximaSaida"
                      type="number"
                      step="0.1"
                      value={formData.correnteMaximaSaida || ""}
                      onChange={(e) => handleInputChange("correnteMaximaSaida", Number(e.target.value))}
                      placeholder="Ex: 20"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="tensaoOperacao">Tensão de operação (V) *</Label>
                    <Input
                      id="tensaoOperacao"
                      type="number"
                      value={formData.tensaoOperacao || ""}
                      onChange={(e) => handleInputChange("tensaoOperacao", Number(e.target.value))}
                      placeholder="Ex: 220"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="frequencia">Frequência (Hz) *</Label>
                    <Input
                      id="frequencia"
                      type="number"
                      value={formData.frequencia || ""}
                      onChange={(e) => handleInputChange("frequencia", Number(e.target.value))}
                      placeholder="Ex: 60"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="fatorPotencia">Fator de potência (%) *</Label>
                    <Input
                      id="fatorPotencia"
                      type="number"
                      step="0.1"
                      value={formData.fatorPotencia || ""}
                      onChange={(e) => handleInputChange("fatorPotencia", Number(e.target.value))}
                      placeholder="Ex: 98"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="tipoConexao">Tipo de conexão *</Label>
                    <Select
                      value={formData.tipoConexao}
                      onValueChange={(value) => setFormData(prev => ({ ...prev, tipoConexao: value }))}
                      required
                    >
                      <SelectTrigger id="tipoConexao">
                        <SelectValue placeholder="Selecione o tipo de conexão" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Monofásico">Monofásico</SelectItem>
                        <SelectItem value="Bifásico">Bifásico</SelectItem>
                        <SelectItem value="Trifásico">Trifásico</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="garantia">Garantia (anos) *</Label>
                    <Input
                      id="garantia"
                      type="number"
                      value={formData.garantia || ""}
                      onChange={(e) => handleInputChange("garantia", Number(e.target.value))}
                      placeholder="Ex: 2"
                      required
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Actions */}
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  <X className="h-4 w-4 mr-2" />
                  Cancelar
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4 mr-2" />
                  )}
                  {editingInversor ? "Atualizar" : "Salvar"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        )}
      </div>

      {/* Table or Cards */}
      <Card>
        <CardHeader>
          <CardTitle>Catálogo de Inversores e Microinversores</CardTitle>
          <CardDescription>
            Lista de todos os inversores e microinversores cadastrados no sistema
          </CardDescription>
        </CardHeader>
        <CardContent>
          {filteredInversores.length === 0 ? (
            <p className="text-center text-muted-foreground">Nenhum inversor encontrado.</p>
          ) : isMobile ? (
            // Vista de cards para mobile
            <div className="space-y-4">
              {filteredInversores.map((inversor) => (
                <Card key={inversor.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="space-y-3">
                      {/* Cabeçalho do card com imagem, marca e modelo */}
                      <div className="flex gap-3 items-start">
                        {inversor.imagemUrl ? (
                          <img
                            src={inversor.imagemUrl}
                            alt={`${inversor.marca} ${inversor.modelo}`}
                            className="w-20 h-20 object-cover rounded shrink-0"
                          />
                        ) : (
                          <div className="w-20 h-20 bg-muted rounded flex items-center justify-center shrink-0">
                            <Zap className="h-8 w-8 text-muted-foreground" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-lg">
                            {inversor.marca}
                          </h3>
                          <p className="text-sm text-muted-foreground truncate">
                            {inversor.modelo}
                          </p>
                          <Badge variant="outline" className="mt-1">
                            {(inversor.potenciaNominal / 1000).toFixed(2)}kW
                          </Badge>
                        </div>
                      </div>
                      
                      {/* Informações do inversor */}
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center justify-between">
                          <span className="text-muted-foreground">Tipo de Conexão:</span>
                          <span className="font-medium">{inversor.tipoConexao}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-muted-foreground">MPPTs:</span>
                          <span className="font-medium">{inversor.mpptIndependentes}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-muted-foreground">Tipo (rede):</span>
                          <span className="font-medium">{LABEL_ARQUITETURA_REDE[inversor.arquiteturaRede]}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-muted-foreground">Tensão Máxima:</span>
                          <span className="font-medium">{inversor.tensaoMaxima}V</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-muted-foreground">Garantia:</span>
                          <span className="font-medium">{inversor.garantia} anos</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-muted-foreground">Micro:</span>
                          <span className="font-medium">{inversor.microinversor ? "Sim" : "Não"}</span>
                        </div>
                      </div>
                      
                      {/* Botões de ação */}
                      {!permissions.isPartner() && (
                        <div className="flex gap-2 pt-2">
                          <Button
                            variant="outline"
                            className="flex-1"
                            size="sm"
                            onClick={() => handleEdit(inversor)}
                          >
                            <Edit className="h-4 w-4 mr-2" />
                            Editar
                          </Button>
                          <Button
                            variant="outline"
                            className="flex-1"
                            size="sm"
                            onClick={() => handleDelete(inversor.id)}
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Excluir
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            // Vista de tabela para desktop
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Imagem</TableHead>
                  <TableHead className={`cursor-pointer ${sortColumn === 'marca' ? 'text-primary' : ''}`} onClick={() => handleSort('marca')}>
                    Marca
                    {sortColumn === 'marca' ? (
                      sortDirection === 'asc' ? <ArrowUp className="ml-2 h-4 w-4 inline" /> : <ArrowDown className="ml-2 h-4 w-4 inline" />
                    ) : (
                      <ArrowUpDown className="ml-2 h-4 w-4 inline" />
                    )}
                  </TableHead>
                  <TableHead className={`cursor-pointer ${sortColumn === 'modelo' ? 'text-primary' : ''}`} onClick={() => handleSort('modelo')}>
                    Modelo
                    {sortColumn === 'modelo' ? (
                      sortDirection === 'asc' ? <ArrowUp className="ml-2 h-4 w-4 inline" /> : <ArrowDown className="ml-2 h-4 w-4 inline" />
                    ) : (
                      <ArrowUpDown className="ml-2 h-4 w-4 inline" />
                    )}
                  </TableHead>
                  <TableHead className={`cursor-pointer ${sortColumn === 'potenciaNominal' ? 'text-primary' : ''}`} onClick={() => handleSort('potenciaNominal')}>
                    Potência Nominal (kW)
                    {sortColumn === 'potenciaNominal' ? (
                      sortDirection === 'asc' ? <ArrowUp className="ml-2 h-4 w-4 inline" /> : <ArrowDown className="ml-2 h-4 w-4 inline" />
                    ) : (
                      <ArrowUpDown className="ml-2 h-4 w-4 inline" />
                    )}
                  </TableHead>
                  <TableHead className={`cursor-pointer ${sortColumn === 'tipoConexao' ? 'text-primary' : ''}`} onClick={() => handleSort('tipoConexao')}>
                    Tipo de Conexão
                    {sortColumn === 'tipoConexao' ? (
                      sortDirection === 'asc' ? <ArrowUp className="ml-2 h-4 w-4 inline" /> : <ArrowDown className="ml-2 h-4 w-4 inline" />
                    ) : (
                      <ArrowUpDown className="ml-2 h-4 w-4 inline" />
                    )}
                  </TableHead>
                  <TableHead className={`cursor-pointer ${sortColumn === 'mpptIndependentes' ? 'text-primary' : ''}`} onClick={() => handleSort('mpptIndependentes')}>
                    MPPTs
                    {sortColumn === 'mpptIndependentes' ? (
                      sortDirection === 'asc' ? <ArrowUp className="ml-2 h-4 w-4 inline" /> : <ArrowDown className="ml-2 h-4 w-4 inline" />
                    ) : (
                      <ArrowUpDown className="ml-2 h-4 w-4 inline" />
                    )}
                  </TableHead>
                  <TableHead className={`cursor-pointer ${sortColumn === 'arquiteturaRede' ? 'text-primary' : ''}`} onClick={() => handleSort('arquiteturaRede')}>
                    Tipo (rede)
                    {sortColumn === 'arquiteturaRede' ? (
                      sortDirection === 'asc' ? <ArrowUp className="ml-2 h-4 w-4 inline" /> : <ArrowDown className="ml-2 h-4 w-4 inline" />
                    ) : (
                      <ArrowUpDown className="ml-2 h-4 w-4 inline" />
                    )}
                  </TableHead>
                  <TableHead>Micro</TableHead>
                  <TableHead>Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredInversores.map((inversor) => (
                  <TableRow key={inversor.id}>
                    <TableCell>
                      {inversor.imagemUrl ? (
                        <img
                          src={inversor.imagemUrl}
                          alt={`${inversor.marca} ${inversor.modelo}`}
                          className="w-16 h-16 object-cover rounded"
                        />
                      ) : (
                        <div className="w-16 h-16 bg-muted rounded flex items-center justify-center">
                          <Zap className="h-6 w-6 text-muted-foreground" />
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="font-medium">{inversor.marca}</TableCell>
                    <TableCell>{inversor.modelo}</TableCell>
                    <TableCell>{(inversor.potenciaNominal / 1000).toFixed(2)}kW</TableCell>
                    <TableCell>{inversor.tipoConexao}</TableCell>
                    <TableCell>{inversor.mpptIndependentes}</TableCell>
                    <TableCell>{LABEL_ARQUITETURA_REDE[inversor.arquiteturaRede]}</TableCell>
                    <TableCell>{inversor.microinversor ? "Sim" : "Não"}</TableCell>
                    <TableCell>
                      {!permissions.isPartner() && (
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEdit(inversor)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDelete(inversor.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Modal de Filtros */}
      <Dialog open={filterOpen} onOpenChange={setFilterOpen}>
        <DialogContent className={isMobile ? 'max-w-[95vw]' : ''}>
          <DialogHeader>
            <DialogTitle>Filtros</DialogTitle>
            <DialogDescription>
              Filtre a lista por marca, tipo de conexão, potência ou tipo de equipamento (microinversor/inversor).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-sm font-medium">Marca</Label>
              <Select value={appliedFilter.marca} onValueChange={(val) => setAppliedFilter(prev => ({ ...prev, marca: val }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione marca" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todas</SelectItem>
                  {uniqueMarcas.sort().map(marca => (
                    <SelectItem key={marca} value={marca.toLowerCase()}>{marca}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm font-medium">Tipo de Conexão</Label>
              <Select value={appliedFilter.tipoConexao} onValueChange={(val) => setAppliedFilter(prev => ({ ...prev, tipoConexao: val }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione tipo de conexão" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  {uniqueTipoConexao.sort().map(tipo => (
                    <SelectItem key={tipo} value={tipo.toLowerCase()}>{tipo}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm font-medium">Potência Nominal</Label>
              <Select value={appliedFilter.potencia} onValueChange={(val) => setAppliedFilter(prev => ({ ...prev, potencia: val }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione potência" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todas</SelectItem>
                  {uniquePotencias.sort((a, b) => a - b).map(potencia => (
                    <SelectItem key={potencia} value={potencia.toString()}>{(potencia / 1000).toFixed(2)}kW</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm font-medium">Tipo de equipamento</Label>
              <Select value={appliedFilter.microinversor || 'todos'} onValueChange={(val) => setAppliedFilter(prev => ({ ...prev, microinversor: val }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Microinversor ou inversor" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="micro">Microinversor</SelectItem>
                  <SelectItem value="inversor">Inversor</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-between pt-4">
              <Button variant="outline" onClick={clearFilters}>Limpar Filtros</Button>
              <Button onClick={applyFilters}>Aplicar Filtros</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
